import { useState, useEffect, useMemo, useCallback } from 'react'
import { favoritesApi } from '../font-explorer/api/favoritesApi'

export function useFavorites(rootElementId: string) {
  const [favoritePairingIds, setFavoritePairingIds] = useState<Set<number>>(new Set())
  const [favoriteFontFamilies, setFavoriteFontFamilies] = useState<Set<string>>(new Set())
  const [loaded, setLoaded] = useState(false)

  // Sorting snapshots — only updated on load, not on individual toggles.
  // This prevents the list from reshuffling while the user is browsing.
  const [pairingSortSnapshot, setPairingSortSnapshot] = useState<Set<number>>(new Set())
  const [fontSortSnapshot, setFontSortSnapshot] = useState<Set<string>>(new Set())

  const authenticated = useMemo(() => {
    return document.getElementById(rootElementId)?.dataset.authenticated === 'true'
  }, [rootElementId])

  useEffect(() => {
    if (!authenticated) return
    favoritesApi
      .load()
      .then((data) => {
        setFavoritePairingIds(data.pairingIds)
        setFavoriteFontFamilies(data.fontFamilies)
        // Set initial sort snapshots
        setPairingSortSnapshot(data.pairingIds)
        setFontSortSnapshot(data.fontFamilies)
      })
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [authenticated])

  const togglePairing = useCallback(async (numericId: number) => {
    if (!authenticated) return
    // Optimistic update — only the live display set, not the sort snapshot
    setFavoritePairingIds((prev) => {
      const next = new Set(prev)
      if (next.has(numericId)) {
        next.delete(numericId)
      } else {
        next.add(numericId)
      }
      return next
    })
    try {
      await favoritesApi.togglePairing(numericId)
    } catch {
      // Revert on error
      setFavoritePairingIds((prev) => {
        const next = new Set(prev)
        if (next.has(numericId)) {
          next.delete(numericId)
        } else {
          next.add(numericId)
        }
        return next
      })
    }
  }, [authenticated])

  const toggleFont = useCallback(async (fontFamily: string) => {
    if (!authenticated) return
    // Optimistic update — only the live display set, not the sort snapshot
    setFavoriteFontFamilies((prev) => {
      const next = new Set(prev)
      if (next.has(fontFamily)) {
        next.delete(fontFamily)
      } else {
        next.add(fontFamily)
      }
      return next
    })
    try {
      await favoritesApi.toggleFont(fontFamily)
    } catch {
      // Revert on error
      setFavoriteFontFamilies((prev) => {
        const next = new Set(prev)
        if (next.has(fontFamily)) {
          next.delete(fontFamily)
        } else {
          next.add(fontFamily)
        }
        return next
      })
    }
  }, [authenticated])

  const isPairingFavorited = useCallback(
    (numericId: number) => favoritePairingIds.has(numericId),
    [favoritePairingIds]
  )

  const isFontFavorited = useCallback(
    (family: string) => favoriteFontFamilies.has(family),
    [favoriteFontFamilies]
  )

  // Sort helpers — use the frozen snapshot so toggling doesn't reshuffle
  const isPairingSortFavorited = useCallback(
    (numericId: number) => pairingSortSnapshot.has(numericId),
    [pairingSortSnapshot]
  )

  const isFontSortFavorited = useCallback(
    (family: string) => fontSortSnapshot.has(family),
    [fontSortSnapshot]
  )

  return {
    authenticated,
    loaded,
    isPairingFavorited,
    isFontFavorited,
    togglePairing,
    toggleFont,
    favoritePairingIds,
    favoriteFontFamilies,
    // Sort-stable versions — won't change mid-browse
    isPairingSortFavorited,
    isFontSortFavorited,
    pairingSortSnapshot,
    fontSortSnapshot,
  }
}
